import { Action, Field } from '@/components/general/list-view/types'
import { createFeatureList } from '../list'

const individualGroup = {
  key: 'individual',
  localeString: {
    sv: 'Individdetaljer',
    en: 'Individual details'
  }
}

const placeGroup = {
  key: 'place',
  localeString: {
    sv: 'Plats',
    en: 'Place'
  }
}

// These fields power the accordion sections in EntityDetails / InfoDisplayDashboard when an
// asset is selected on the map. Also imported directly by AssetDashboard.vue to derive CSV export
// columns for the "individual" group, so adding a `labels.*` field with a translationKey here
// makes it exportable without touching the CSV column list.
export const fields: Field[] = [
  {
    key: 'categories.0.name',
    type: 'text',
    showInViews: ['minimized', 'fullscreen'],
    localeString: { en: 'Category', sv: 'Kategori' },
    sortable: false
  },
  {
    key: 'categories.1.name',
    showInViews: ['minimized', 'fullscreen'],
    type: 'text',
    localeString: { en: 'Subcategory', sv: 'Underkategori' },
    sortable: false
  },
  {
    key: 'name',
    type: 'text',
    showInViews: ['minimized', 'fullscreen'],
    localeString: { en: 'Name', sv: 'Namn' },
    sortDirection: 'asc'
  },
  {
    key: 'labels.description',
    type: 'text',
    area: true,
    localeString: { en: 'Description', sv: 'Beskrivning' }
  },
  {
    key: 'createdDate',
    type: 'date',
    format: {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    },
    localeString: { en: 'Created', sv: 'Skapades' }
  },
  {
    key: 'lastModifiedDate',
    type: 'date',
    format: {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    },
    localeString: { en: 'Updated', sv: 'Uppdaterades' }
  },
  {
    key: 'point.0.height',
    type: 'number',
    format: { maximumFractionDigits: 2 },
    group: placeGroup,
    localeString: { en: 'Height (RH2000)', sv: 'Höjd (RH2000)' }
  },
  {
    key: 'labels.estimatedHeight',
    type: 'number',
    format: { maximumFractionDigits: 2 },
    group: placeGroup,
    localeString: { en: 'Height from floor (m)', sv: 'Höjd från golv (m)' }
  },
  {
    key: 'point.0.geometry.coordinates',
    type: 'coordinates',
    group: placeGroup,
    localeString: { en: 'Coordinates', sv: 'Koordinater' }
  },
  {
    key: 'mainPath',
    type: 'path',
    group: placeGroup,
    localeString: { en: 'Placement', sv: 'Placering' }
  },
  {
    key: 'labels.externalId',
    type: 'text',
    translationKey: 'general__component__label_externalid',
    group: individualGroup
  },
  {
    key: 'labels.internalId',
    type: 'text',
    translationKey: 'general__component__label_internalid',
    group: individualGroup
  },
  {
    key: 'labels.ownershipType',
    type: 'text',
    translationKey: 'general__component__label_ownershiptype',
    group: individualGroup
  },
  {
    key: 'labels.monetaryValue',
    type: 'text',
    translationKey: 'general__component__label_monetaryvalue',
    group: individualGroup
  },
  {
    key: 'labels.condition',
    type: 'text',
    translationKey: 'general__component__label_condition',
    group: individualGroup
  },
  {
    key: 'labels.dangerousSubstancesSuspected',
    type: 'text',
    translationKey: 'general__component__label_dangeroussubstancessuspected',
    group: individualGroup
  },
  {
    key: 'labels.suitableForReuse',
    type: 'text',
    translationKey: 'general__component__label_suitableforreuse',
    group: individualGroup
  },
  {
    key: 'labels.recommendedAction',
    type: 'text',
    translationKey: 'general__component__label_recommendedaction',
    group: individualGroup
  },
  {
    key: 'labels.installationDate',
    type: 'text',
    translationKey: 'general__component__label_installationdate',
    group: individualGroup
  }
]

const actions: Action[] = [
  {
    actionName: 'new',
    type: 'new',
    labelKey: 'general__text__new',
    icon: 'IconPlusSquareSmall',
    hiddenInToolbar: true,
    access: 'CREATOR'
  },
  {
    actionName: 'edit',
    type: 'single',
    icon: 'IconEditSmall',
    labelKey: 'general__text__change',
    access: 'EDITOR',
    hiddenInModal: true,
    replaces: ['view']
  },
  {
    actionName: 'view',
    type: 'single',
    hiddenInModal: true,
    icon: 'IconFileSearch',
    labelKey: 'general__text__open',
    access: 'READER'
  },
  {
    actionName: 'move',
    type: 'custom',
    labelKey: 'general__text__move',
    icon: 'IconMoveSmall',
    access: 'EDITOR'
  },
  {
    actionName: 'copy',
    type: 'custom',
    labelKey: 'general__text__copy',
    icon: 'IconCopySmall',
    access: 'CREATOR'
  },
  {
    actionName: 'delete',
    type: 'bulk',
    labelKey: 'general__text__delete',
    icon: 'IconTrash2Small',
    access: 'ADMIN'
  },
  {
    actionName: 'download-csv',
    type: 'custom',
    labelKey: 'general__text__download-csv',
    icon: 'IconDownload',
    access: 'READER',
    hiddenInModal: true
  }
]

export const assetList = createFeatureList({
  type: 'ASSET',
  icon: 'IconMark',
  title: {
    en: 'Assets',
    sv: 'Tillgångar'
  },
  component: 'AssetDashboard',
  fields,
  actions
})
